import {
  ComponentItemType,
  EditConfigType,
  EditorFlagEum,
  GlobalConfigType,
  SourceTypes,
} from '@/typings/types'
import { EditorState } from '@/redux/reducers/editor/types'
import { RecursivePartial } from '@/typings/utils'
import { getActivityDetail } from '@/pages/apis'
import { createHash, getQueryParams } from '@/utils/index'
import { prefetchDataMap } from '@/components/index'
import { IncomingMessage } from 'http'
import logger from '../../../server/logger'

export const changeEditConfigType = 'editor/changeEditConfig'
export const changeStatusType = 'editor/changeStatus'
export const changeTypeType = 'editor/changeType'
export const changeEnvType = 'editor/changeEnv'
export const addType = 'editor/add'
export const changePropsType = 'editor/changeProps'
export const addComponentByIndexType = 'editor/addComponentByIndex'
export const replaceComponentByIndexType = 'editor/replaceComponentByIndex'
export const replaceComponentListType = 'editor/replaceComponentList'
export const moveComponentType = 'editor/moveComponent'
export const copyComponentType = 'editor/copyComponent'
export const deleteComponentType = 'editor/deleteComponent'
export const changeGlobalConfigType = 'editor/changeGlobalConfig'
export const setStateType = 'editor/setState'
export const deepSetStateType = 'editor/deepSetState'

export interface ChangeEditConfigAction {
  type: typeof changeEditConfigType
  payload: EditConfigType
}

export interface SetStateTypeAction {
  type: typeof setStateType
  payload: Partial<EditorState>
}

export interface DeepSetStateTypeAction {
  type: typeof deepSetStateType
  payload: RecursivePartial<EditorState>
}

export interface AddTypeAction {
  type: typeof addType
  payload: ComponentItemType
}

export interface ChangePropsAction {
  type: typeof changePropsType
  payload: {
    index: number
    props: any
  }
}

export interface AddComponentByIndexAction {
  type: typeof addComponentByIndexType
  payload: {
    index: number
    props: any
    name: string
  }
}

export interface ReplaceComponentByIndexAction {
  type: typeof replaceComponentByIndexType
  payload: {
    index: number
    props: any
    name: string
  }
}

export interface MoveComponentTypeAction {
  type: typeof moveComponentType
  payload: {
    index: number
    props: any
    name: string
    activeIndex: number
  }
}

export interface CopyComponentTypeAction {
  type: typeof copyComponentType
  payload: {
    index: number
  }
}

export interface DeleteComponentTypeAction {
  type: typeof deleteComponentType
  payload: {
    index: number
  }
}

export interface ChangeGlobalConfigTypeAction {
  type: typeof changeGlobalConfigType
  payload: GlobalConfigType
}

export interface ReplaceComponentListTypeAction {
  type: typeof replaceComponentListType
  payload: ComponentItemType[]
}

export interface ChangeEditConfigTypeAction {
  type: typeof changeEditConfigType
  payload: EditConfigType
}

export type EditorAction =
  | ChangeEditConfigAction
  | DeepSetStateTypeAction
  | SetStateTypeAction
  | AddTypeAction
  | ChangePropsAction
  | AddComponentByIndexAction
  | ReplaceComponentByIndexAction
  | MoveComponentTypeAction
  | CopyComponentTypeAction
  | DeleteComponentTypeAction
  | ChangeGlobalConfigTypeAction
  | ReplaceComponentListTypeAction
  | ChangeEditConfigTypeAction

/************************************** 以上为action 以下为 action**************************************************** */

// 有副作用
async function getComponentsPrefetchDataWithEffect({
  componentList,
  editConfig,
  globalConfig,
  req,
}: {
  componentList: ComponentItemType<any>[]
  editConfig: EditConfigType
  globalConfig: GlobalConfigType
  req: IncomingMessage
}): Promise<ComponentItemType<any>[]> {
  // 只是修改值 没有必要使用Promise.all 获取结果
  const reqArr: Promise<any>[] = []
  //
  const baseParams = {
    editConfig,
    globalConfig,
    req,
  }
  componentList.forEach((ele) => {
    const { name } = ele
    if (prefetchDataMap[name] && typeof prefetchDataMap[name] === 'function') {
      const p = prefetchDataMap[name]({
        ...baseParams,
        propsConfig: ele.propsConfig,
      }).then((result) => {
        ele.prefetchData = result
      })
      reqArr.push(p)
    }
  })
  await Promise.all(reqArr)

  return componentList
}

function formatComponentList({
  componentList,
  editConfig,
}: {
  componentList: ComponentItemType<any>[]
  editConfig: EditConfigType
}) {
  return componentList.map((ele) => {
    const { props } = ele
    return {
      ...ele,
      hash: createHash(),
      propsConfig: props[editConfig.lang] || props['zh-TW'] || props['zh'] || {},
    }
  })
}

let createdPrefetchDataHistogram = Function.prototype

if (typeof window === 'undefined') {
  createdPrefetchDataHistogram = require('../../../server/scripts/prometheus')
    .createdPrefetchDataHistogram
}

/**
 * 逻辑一道 action actionCreator
 * // 副作用： jockey
 * @param editConfig
 */
export const getInitPageAction = async ({
  editConfig,
  req,
}: {
  editConfig: EditConfigType
  req?: IncomingMessage
}): Promise<SetStateTypeAction> => {
  let start = null
  if (typeof window === 'undefined') {
    start = process.hrtime()
    console.log('start', start)
  }

  const result = await getActivityDetail(editConfig.id, SourceTypes.fromCDN)
  const { data } = result
  const { componentList, globalConfig, share = {} } =
    editConfig.editorFlag === EditorFlagEum.preview ? data.previewData : data.data
  let newComponentsList: ComponentItemType[] = formatComponentList({
    componentList,
    editConfig,
  })

  // // client端也是有场景的。 到那时就解开封印
  if (req) {
    newComponentsList = await getComponentsPrefetchDataWithEffect({
      editConfig,
      globalConfig,
      req,
      componentList: newComponentsList,
    })
  }
  if (typeof window === 'undefined') {
    try {
      const delta = process.hrtime(start)
      // Format to high resolution time with nano time
      const hrtime = delta[0] * 1000 + delta[1] / 1000000
      if (delta[0] > 1.5) {
        logger('cdn配置预取RT大于1.5s').error(req.url)
      }
      // @ts-ignore
      createdPrefetchDataHistogram().labels('/prefetch data/*').observe(hrtime)
    } catch (error) {}
  }

  const query = getQueryParams(req?.url)
  return {
    type: setStateType,
    payload: {
      editConfig: {
        ...editConfig,
        query,
      },
      globalConfig,
      type: data.type,
      componentList: newComponentsList,
      share,
      status: data.status,
    },
  }
}

export const addCreator = (item: ComponentItemType): AddTypeAction => ({
  type: addType,
  payload: item,
})

export const changePropsCreator = ({
  index,
  props,
}: {
  index: number
  props: any
}): ChangePropsAction => ({
  type: changePropsType,
  payload: {
    index,
    props,
  },
})

export const addComponentByIndexCreator = ({
  index,
  props,
  name,
}: {
  index: number
  props: any
  name: string
}): AddComponentByIndexAction => ({
  type: addComponentByIndexType,
  payload: {
    index,
    props,
    name,
  },
})

export const replaceComponentByIndexCreator = ({
  index,
  props,
  name,
}: {
  index: number
  props: any
  name: string
}): ReplaceComponentByIndexAction => ({
  type: replaceComponentByIndexType,
  payload: {
    index,
    props,
    name,
  },
})

export const moveComponentTypeCreator = ({
  index,
  props,
  name,
  activeIndex,
}: {
  index: number
  props: any
  name: string
  activeIndex: number
}): MoveComponentTypeAction => ({
  type: moveComponentType,
  payload: {
    index,
    props,
    name,
    activeIndex,
  },
})

export const copyComponentTypeCreator = ({
  index,
}: {
  index: number
}): CopyComponentTypeAction => ({
  type: copyComponentType,
  payload: {
    index,
  },
})

export const deleteComponentTypeCreator = ({
  index,
}: {
  index: number
}): DeleteComponentTypeAction => ({
  type: deleteComponentType,
  payload: {
    index,
  },
})

export const changeGlobalConfigTypeCreator = (
  payload: GlobalConfigType
): ChangeGlobalConfigTypeAction => ({
  type: changeGlobalConfigType,
  payload,
})

export const replaceComponentListTypeCreator = (
  payload: ComponentItemType[]
): ReplaceComponentListTypeAction => ({
  type: replaceComponentListType,
  payload,
})

export const changeEditConfigTypeCreator = (
  payload: EditConfigType
): ChangeEditConfigTypeAction => ({
  type: changeEditConfigType,
  payload,
})
